// Recommendation pipeline. Deterministic and explainable:
//
//   1. Hard constraints   diet and allergy rules, dislikes, hidden recipes, missing equipment
//   2. Pantry eligibility what's owned, staples, safe substitutions (ranking.ts)
//   3. Personal ranking   time, skill, cuisine, heat, goals, budget, learned signals, variety
//   4. Explanation        only reasons backed by the user's own data or the recipe's facts
//
// Feedback is captured in the store; learn.ts turns it into the `learned` input here.

import { rankRecipes, type RankedRecipe } from './ranking';
import type { StockItem } from './inventory';
import { RECIPES, type MealType } from './recipes';
import { extraBreaksRule, generatedRecipes, isGenerated } from './generated';
import { RECIPE_META, CUISINE_LABELS, cuisineMatches } from './recipeMeta';
import { goalFit } from './nutrition/plate';
import { estimateMeal } from './nutrition/estimate';
import { matchedLikes } from './likes';
import { FOOD_LIKES, FRUSTRATION_PRIORITY, type Priority } from './preferences';
import { missingCost } from './ingredientCost';
import { mainProtein } from './ranking';
import { displayName, type IngredientId } from './taxonomy';
import { joinNatural } from './text';
import { EMPTY_LEARNED, type Learned } from './learn';
import type { Profile } from '../state/schema';

export interface ScoreComponent {
  key: string;
  points: number;
  /** User-facing explanation; null for components that only adjust order. */
  reason: string | null;
}

/**
 * What we know about the user's kitchen for this recommendation:
 *   known    the user entered items, so have/need is real
 *   unknown  nothing entered yet; have/need would be a guess, so the UI shows none
 *   ignored  occasion cooking: the user will buy everything, so every required ingredient is "need"
 */
export type KitchenMode = 'known' | 'unknown' | 'ignored';

export interface Recommendation extends RankedRecipe {
  kitchenMode: KitchenMode;
  personal: number;
  components: ScoreComponent[];
  /** Up to two short reasons, strongest first. */
  reasons: string[];
}

export interface RecommendInput {
  profile: Profile;
  kitchen: IngredientId[];
  useSoon: IngredientId[];
  recentlyCooked: string[];
  /** Recipes shown "not today" in the last day; kept out of the top slot. */
  recentlyDeclined?: string[];
  learned?: Learned;
  meals?: MealType[];
  /** Amounts the user entered for kitchen items. */
  stock?: StockItem[];
  /** Occasion cooking: rank on taste and goals alone, and treat every ingredient as to buy. */
  ignoreKitchen?: boolean;
}

/** Recipes the user's kitchen equipment can't make. */
export function needsMissingEquipment(recipeId: string, equipment: Profile['equipment']): boolean {
  const need = RECIPE_META[recipeId]?.equipment ?? [];
  return need.some((e) => !equipment.includes(e));
}

export function recommend(input: RecommendInput): Recommendation[] {
  const { profile, learned = EMPTY_LEARNED } = input;
  const meals = new Set(input.meals ?? ['main']);
  // The collection, plus recipes Amari wrote for this person whose unlisted ingredients fit their rules.
  const written = generatedRecipes().filter((g) => !extraBreaksRule(g.extras, profile.rules)).map((g) => g.recipe);
  const pool = [...RECIPES, ...written].filter((r) =>
    meals.has(r.meal) && !profile.hiddenRecipes.includes(r.id) && !needsMissingEquipment(r.id, profile.equipment));

  // With no kitchen entered, staples alone would make "have" and "need" look known when they aren't.
  const kitchenMode: KitchenMode = input.ignoreKitchen ? 'ignored' : input.kitchen.length ? 'known' : 'unknown';
  const useKitchen = kitchenMode === 'known';
  const ranked = rankRecipes(pool, {
    kitchen: useKitchen ? input.kitchen : [],
    staples: useKitchen ? profile.staples : [],
    useSoon: useKitchen ? input.useSoon : [],
    rules: profile.rules,
    dislikes: profile.dislikes,
    styles: profile.styles,
    recentlyCooked: input.recentlyCooked,
    stock: useKitchen ? input.stock : [],
    servings: profile.household,
  });

  const declined = new Set(input.recentlyDeclined ?? []);
  // Every recommendation carries calories, carbs and protein per serving; a meal we can't estimate isn't offered.
  // (Amari's own recipes stay even when an unlisted ingredient leaves them without an estimate.)
  const out = ranked.filter((r) => estimateMeal(r) !== null || isGenerated(r.recipe.id)).map((r): Recommendation => {
    const c: ScoreComponent[] = [];
    const meta = RECIPE_META[r.recipe.id];
    const kitchenCount = r.usesFromKitchen.length;

    // Pantry: the base score from ranking.ts already rewards makeable and use-soon; explain it.
    c.push({ key: 'base', points: r.score, reason: null });
    if (r.usesSoon.length) {
      c.push({ key: 'use_soon', points: 0, reason: `Uses your ${joinNatural(r.usesSoon.slice(0, 2).map(displayName))} while it’s fresh` });
    }
    if (r.makeable && kitchenCount >= 2) c.push({ key: 'have_all', points: 0, reason: `You have everything, ${kitchenCount} from your kitchen` });
    else if (r.makeable) c.push({ key: 'have_all', points: 0, reason: 'You have everything you need' });
    else if (kitchenCount >= 2) c.push({ key: 'uses_kitchen', points: 0, reason: `Uses ${kitchenCount} things you already have` });

    // Time and skill
    const over = r.recipe.minutes - profile.maxMinutes;
    if (over <= 0) c.push({ key: 'time', points: 6, reason: profile.maxMinutes < 60 ? `Ready in ${r.recipe.minutes} min, within your ${profile.maxMinutes}-minute limit` : null });
    else c.push({ key: 'time', points: -Math.min(30, over * 1.2), reason: null });
    if (profile.skill === 'new' && r.recipe.difficulty === 'medium') c.push({ key: 'skill', points: -10, reason: null });

    // Taste
    if (meta && cuisineMatches(profile.cuisines, meta.cuisine)) c.push({ key: 'cuisine', points: 8, reason: `${CUISINE_LABELS[meta.cuisine]}, a cuisine you like` });
    const liked = matchedLikes(r.recipe, profile.likes);
    if (liked.length) {
      const label = FOOD_LIKES.find((f) => f.id === liked[0])!.label.toLowerCase();
      c.push({ key: 'likes', points: 8 + 3 * (liked.length - 1), reason: `${label.charAt(0).toUpperCase()}${label.slice(1)}, which you enjoy` });
    }
    if (meta && meta.heat === 2 && profile.heat === 'none') c.push({ key: 'heat', points: -25, reason: null });
    if (meta && meta.heat === 2 && profile.heat === 'hot') c.push({ key: 'heat', points: 4, reason: null });

    // Goals: plate facts mapped through cited rules (knowledge layer).
    const fit = goalFit(r.recipe, profile.goals);
    if (fit.points) c.push({ key: `goal:${fit.ruleId}`, points: fit.points, reason: fit.reason });

    // Priorities and budget
    // Frustrations from onboarding count as the priority they imply ("groceries cost too much").
    const pr = new Set<Priority>([...profile.priorities, ...profile.frustrations.map((f) => FRUSTRATION_PRIORITY[f]).filter((x): x is Priority => !!x)]);
    // "Eat more balanced meals" without a stated goal: rank balanced plates up, as the balanced goal would.
    if (pr.has('nutrition') && !profile.goals.length) {
      const balanced = goalFit(r.recipe, ['balanced']);
      if (balanced.points > 0) c.push({ key: 'nutrition', points: Math.min(6, balanced.points), reason: null });
    }
    if (pr.has('convenience') && r.recipe.minutes <= 20) c.push({ key: 'convenience', points: 6, reason: null });
    if (pr.has('less_waste') && r.usesFromKitchen.length) c.push({ key: 'less_waste', points: Math.min(12, r.usesFromKitchen.length * 3), reason: null });
    if (profile.budget === 'tight' || pr.has('affordability')) {
      c.push({ key: 'budget', points: -missingCost(r.missing) * 3 + (r.recipe.tags.includes('budget') ? 5 : 0), reason: null });
    }

    // Learned behaviour (only when enabled; respects user overrides inside learn.ts).
    const affinity = learned.recipe.get(r.recipe.id) ?? 0;
    if (affinity) c.push({ key: 'recipe_affinity', points: Math.max(-30, Math.min(20, affinity * 12)), reason: null });
    const fact = learned.recipeFacts.get(r.recipe.id);
    if (learned.enabled && fact === 'loved') c.push({ key: 'loved', points: 0, reason: 'You rated it Make again' });
    const protein = mainProtein(r.recipe);
    const pAff = protein ? learned.effects.protein.get(protein) ?? 0 : 0;
    if (pAff) c.push({ key: 'learned_protein', points: pAff * 10, reason: pAff > 0.4 && protein ? `You’ve enjoyed ${displayName(protein)} dishes` : null });
    const cAff = meta ? learned.effects.cuisine.get(meta.cuisine) ?? 0 : 0;
    if (cAff) c.push({ key: 'learned_cuisine', points: cAff * 8, reason: cAff > 0.4 && meta ? `Similar to ${CUISINE_LABELS[meta.cuisine]} meals you liked` : null });
    if (learned.effects.preferQuick) {
      c.push({ key: 'learned_quick', points: r.recipe.minutes <= 20 ? 8 * learned.effects.preferQuick : r.recipe.minutes >= 35 ? -10 * learned.effects.preferQuick : 0, reason: null });
    }
    if (learned.effects.avoidHeat && meta?.heat) c.push({ key: 'learned_heat', points: -12 * meta.heat * learned.effects.avoidHeat, reason: null });
    const wastedNeeded = r.missing.filter((m) => learned.effects.wasted.includes(m));
    if (wastedNeeded.length) c.push({ key: 'learned_waste', points: -8 * wastedNeeded.length, reason: null });

    if (declined.has(r.recipe.id)) c.push({ key: 'declined', points: -60, reason: null });

    const personal = c.reduce((s, x) => s + x.points, 0);
    const reasons = c
      .filter((x) => x.reason)
      .sort((a, b) => reasonRank(a) - reasonRank(b))
      .map((x) => x.reason!)
      .slice(0, 2);
    return { ...r, kitchenMode, personal, components: c, reasons };
  });

  return out.sort((a, b) => (a.makeable === b.makeable ? b.personal - a.personal : a.makeable ? -1 : 1));
}

/** Order in which reasons are worth reading: the user's own signals first. */
const REASON_ORDER = ['use_soon', 'loved', 'learned_protein', 'learned_cuisine', 'goal', 'likes', 'time', 'cuisine', 'have_all', 'uses_kitchen'];
function reasonRank(c: ScoreComponent): number {
  const i = REASON_ORDER.findIndex((k) => c.key === k || c.key.startsWith(`${k}:`));
  return i < 0 ? 99 : i;
}

/** "Not feeling it" alternatives: next best, spread across proteins and cuisines. */
export function alternatives(recs: Recommendation[], excludeId: string | null, n = 6): Recommendation[] {
  return diversify(recs.filter((r) => r.recipe.id !== excludeId && r.makeable), n, excludeId ? recs.find((r) => r.recipe.id === excludeId) : undefined);
}

/** Meals one or two cheap ingredients away, cheapest gap first. Uses the real missing list. */
export function almostThere(recs: Recommendation[], n = 6): Recommendation[] {
  return recs
    .filter((r) => !r.makeable && r.missing.length >= 1 && r.missing.length <= 2 && r.usesFromKitchen.length > 0)
    .sort((a, b) => missingCost(a.missing) - missingCost(b.missing) || b.personal - a.personal)
    .slice(0, n);
}

/** Small, stable per-day nudge in [-1, 1) so close calls rotate from one day to the next. */
function dayJitter(seed: string, id: string): number {
  let h = 2166136261;
  for (const ch of `${seed}|${id}`) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  return ((h >>> 0) % 2000) / 1000 - 1;
}

/**
 * Tonight's three dinners: the best mains, spread across proteins and cuisines, rotating day to day
 * among close calls so the same three don't sit there all week. Ready-to-cook meals come first
 * (then the fewest missing); a planned dinner, if any, is passed in as `lead` and comes first.
 * `round` gives a fresh three when the person asks for others.
 */
export function dailyPicks(recs: Recommendation[], day: string, opts: { n?: number; round?: number; lead?: Recommendation } = {}): Recommendation[] {
  const { n = 3, round = 0, lead } = opts;
  const mains = recs.filter((r) => r.recipe.meal === 'main' && r.recipe.id !== lead?.recipe.id);
  const known = mains.some((r) => r.kitchenMode === 'known');
  const ready = known ? mains.filter((r) => r.makeable) : [];
  // Not enough ready to cook: top up with the closest (fewest missing), as dinners a short shop away.
  const closest = known ? mains.filter((r) => !r.makeable).sort((a, b) => a.missing.length - b.missing.length || b.personal - a.personal) : [];
  const pool = known ? [...ready, ...closest.slice(0, Math.max(0, n * 4 - ready.length))] : mains;
  const seed = `${day}#${round}`;
  // Jitter only reorders near-equals (a few points); learned likes, goals and use-soon still win.
  const scored = pool.map((r) => ({ ...r, personal: r.personal + dayJitter(seed, r.recipe.id) * 6 + (r.makeable ? 1000 : -r.missing.length * 40) }));
  const skip = new Set<string>();
  // Each "show me others" round steps past the previous rounds' picks.
  for (let i = 0; i < round; i++) for (const r of diversify(scored.filter((x) => !skip.has(x.recipe.id)), n - (lead ? 1 : 0))) skip.add(r.recipe.id);
  let rest = scored.filter((r) => !skip.has(r.recipe.id));
  if (rest.length < n) rest = scored;
  const picked = diversify(rest, n - (lead ? 1 : 0), lead).map((p) => recs.find((r) => r.recipe.id === p.recipe.id)!);
  return lead ? [lead, ...picked] : picked;
}

/** Greedy pick that avoids repeating the same protein or cuisine back to back. */
export function diversify(recs: Recommendation[], n: number, seed?: Recommendation): Recommendation[] {
  const out: Recommendation[] = [];
  const pool = [...recs];
  const proteinCount = new Map<string, number>();
  const cuisineCount = new Map<string, number>();
  const note = (r: Recommendation) => {
    const p = mainProtein(r.recipe) ?? 'none';
    const c = RECIPE_META[r.recipe.id]?.cuisine ?? 'everyday';
    proteinCount.set(p, (proteinCount.get(p) ?? 0) + 1);
    cuisineCount.set(c, (cuisineCount.get(c) ?? 0) + 1);
  };
  if (seed) note(seed);
  while (out.length < n && pool.length) {
    let bestIdx = 0, best = -Infinity;
    pool.forEach((r, i) => {
      const p = mainProtein(r.recipe) ?? 'none';
      const c = RECIPE_META[r.recipe.id]?.cuisine ?? 'everyday';
      const penalty = (proteinCount.get(p) ?? 0) * 18 + (cuisineCount.get(c) ?? 0) * 8;
      const v = r.personal - penalty;
      if (v > best) { best = v; bestIdx = i; }
    });
    const [pick] = pool.splice(bestIdx, 1);
    out.push(pick);
    note(pick);
  }
  return out;
}
